import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { useSettings } from '@/context/SettingsContext';

const Footer = () => {
  const { settings } = useSettings();
  return (
    <footer className="footer-dark">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
          <div>
            <img 
              src={settings.logo_url || "https://customer-assets.emergentagent.com/job_34f43037-f49f-4896-a120-3461406a9d35/artifacts/wh0yojmq_e30d7691-c116-46a6-b645-fa81d1964b10-removebg-preview.png"} 
              alt="Infinity Group - Dreams Unlimited" 
              className="h-32 w-auto mb-4"
            />
            <p className="text-sm leading-relaxed mb-4">
              {settings.footer_description}
            </p>
            <div className="flex gap-3 mt-6">
              <a href="#" className="w-10 h-10 rounded-full bg-gray-700 hover:bg-red-700 flex items-center justify-center transition-colors">
                <Facebook size={18} />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-gray-700 hover:bg-red-700 flex items-center justify-center transition-colors">
                <Twitter size={18} />
              </a>
              <a href="https://www.instagram.com/infinity_group.ngp?igsh=Z3V2NG1vZGFydHYz" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-700 hover:bg-red-700 flex items-center justify-center transition-colors">
                <Instagram size={18} />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-gray-700 hover:bg-red-700 flex items-center justify-center transition-colors">
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/" className="hover:text-red-500 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/projects" className="hover:text-red-500 transition-colors">
                  Projects
                </Link>
              </li>
              <li>
                <Link to="/why-us" className="hover:text-red-500 transition-colors">
                  Why Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-red-500 transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Projects</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/projects?status=upcoming" className="hover:text-red-500 transition-colors">
                  Upcoming Projects
                </Link>
              </li>
              <li>
                <Link to="/projects?status=ongoing" className="hover:text-red-500 transition-colors">
                  Ongoing Projects
                </Link>
              </li>
              <li>
                <Link to="/projects?status=completed" className="hover:text-red-500 transition-colors">
                  Completed Projects
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-lg mb-4">Contact Info</h4>
            <ul className="space-y-4 text-sm">
              {settings.show_phone_numbers !== false && settings.founders && settings.founders.length > 0 && (
                settings.founders.map((founder, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Phone size={18} className="mt-0.5" style={{ color: settings.primary_color || '#C41E3A' }} />
                    <div>
                      <p className="text-white font-medium">{founder.name}</p>
                      <a href={`tel:${founder.phone}`} className="hover:text-red-500 transition-colors">
                        {founder.phone}
                      </a>
                    </div>
                  </li>
                ))
              )}
              {settings.show_email !== false && (
                <li className="flex items-start gap-3">
                  <Mail size={18} className="mt-0.5" style={{ color: settings.primary_color || '#C41E3A' }} />
                  <a href={`mailto:${settings.contact_email || 'info@infinitygroup.com'}`} className="hover:text-red-500 transition-colors">
                    {settings.contact_email || 'info@infinitygroup.com'}
                  </a>
                </li>
              )}
              {settings.show_address !== false && (
                <li className="flex items-start gap-3">
                  <MapPin size={18} className="mt-0.5" style={{ color: settings.primary_color || '#C41E3A' }} />
                  <a
                    href={
                      settings.contact_address_map_url ||
                      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(settings.contact_address || 'Nagpur, Maharashtra, India')}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-red-500 transition-colors"
                    data-testid="footer-address-link"
                  >
                    {settings.contact_address || 'Nagpur, Maharashtra, India'}
                  </a>
                </li>
              )}
              {settings.show_instagram !== false && (
                <li className="flex items-start gap-3">
                  <Instagram size={18} className="mt-0.5" style={{ color: settings.primary_color || '#C41E3A' }} />
                  <a href={settings.instagram_url || 'https://www.instagram.com/infinity_group.ngp'} target="_blank" rel="noopener noreferrer" className="hover:text-red-500 transition-colors">
                    {settings.instagram_handle || '@infinity_group.ngp'}
                  </a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-700 pt-8 text-center">
          <p className="text-sm">
            &copy; {new Date().getFullYear()} Infinity Group. All rights reserved. |
            <Link to="/" className="hover:text-red-500 ml-2">Privacy Policy</Link> |
            <Link to="/" className="hover:text-red-500 ml-2">Terms & Conditions</Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;